import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/hooks/useAuth";
import { useTurnstile } from "@/lib/turnstile";
import { AuthLayout } from "@/components/layouts/AuthLayout";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";

const RESEND_COOLDOWN_SECONDS = 60;

export function Signup() {
  const navigate = useNavigate();
  const { session, loading: authLoading } = useAuth();

  const [email, setEmail] = useState("");
  const [orgName, setOrgName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState<{ email: string } | null>(null);
  const [resendCooldown, setResendCooldown] = useState(0);

  // The widget moves between the form view and the "Check your inbox"
  // resend view; the phase key triggers a re-mount into the new
  // container. Replaced what used to be ~50 lines of inline mount /
  // reset code in this file.
  const turnstile = useTurnstile(submitted ? "submitted" : "form");
  const { siteKey: turnstileSiteKey, captchaToken } = turnstile;

  // If the user is already signed in, the dashboard is the right place
  // to be — keep this page strictly for the logged-out flow.
  useEffect(() => {
    if (!authLoading && session) {
      navigate("/", { replace: true });
    }
  }, [session, authLoading, navigate]);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setInterval(() => {
      setResendCooldown((s) => Math.max(0, s - 1));
    }, 1000);
    return () => {
      clearInterval(timer);
    };
  }, [resendCooldown]);

  async function sendMagicLink(targetEmail: string, targetOrgName: string) {
    const { error: otpError } = await supabase.auth.signInWithOtp({
      email: targetEmail,
      options: {
        shouldCreateUser: true,
        captchaToken: captchaToken ?? undefined,
        emailRedirectTo: `${window.location.origin}/signup/finalize`,
        data: { pending_org_name: targetOrgName },
      },
    });
    if (otpError) throw otpError;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (turnstileSiteKey && !captchaToken) {
      setError("Please complete the captcha.");
      return;
    }
    setSubmitting(true);
    try {
      await sendMagicLink(email, orgName);
      setSubmitted({ email });
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Signup failed");
    } finally {
      // Always reset — the token has been consumed either way, and a
      // resend (or a retry after error) needs a fresh one.
      turnstile.reset();
      setSubmitting(false);
    }
  }

  async function handleResend() {
    if (!submitted || resendCooldown > 0) return;
    // Turnstile tokens are single-use; the previous one was burnt by
    // the original submit. Require a fresh challenge before re-sending
    // so the resend path doesn't bypass the captcha.
    if (turnstileSiteKey && !captchaToken) {
      setError("Please complete the captcha to resend.");
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      await sendMagicLink(submitted.email, orgName);
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Resend failed");
    } finally {
      turnstile.reset();
      setSubmitting(false);
    }
  }

  if (submitted) {
    const resendDisabled =
      submitting ||
      resendCooldown > 0 ||
      (Boolean(turnstileSiteKey) && !captchaToken);
    return (
      <AuthLayout>
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Check your inbox</CardTitle>
            <CardDescription>
              We sent a magic link to <strong>{submitted.email}</strong>. Click
              it to finish creating your workspace.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
          </CardContent>
          <CardFooter className="flex flex-col gap-2">
            <div className="flex justify-between w-full">
              <Link
                to="/login"
                className="text-sm text-muted-foreground underline underline-offset-2 decoration-accent-mint hover:text-foreground transition-colors"
              >
                Back to login
              </Link>
              <Button
                variant="outline"
                onClick={handleResend}
                disabled={resendDisabled}
              >
                {resendCooldown > 0
                  ? `Resend in ${String(resendCooldown)}s`
                  : submitting
                    ? "Sending..."
                    : "Resend"}
              </Button>
            </div>
            {turnstileSiteKey && (
              <>
                <div ref={turnstile.ref} data-testid="turnstile-widget" />
                {turnstile.failed && (
                  <Alert variant="destructive">
                    <AlertDescription>
                      Couldn&apos;t load the security check. Please refresh the
                      page or check your network connection.
                    </AlertDescription>
                  </Alert>
                )}
              </>
            )}
          </CardFooter>
        </Card>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Create your workspace</CardTitle>
          <CardDescription>
            Enter your email and organisation name to get started.
          </CardDescription>
        </CardHeader>
        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                }}
                required
                autoComplete="email"
                disabled={submitting}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="orgName">Organisation name</Label>
              <Input
                id="orgName"
                type="text"
                value={orgName}
                onChange={(e) => {
                  setOrgName(e.target.value);
                }}
                required
                minLength={1}
                maxLength={255}
                disabled={submitting}
              />
            </div>
          </CardContent>
          <CardFooter className="flex flex-col gap-2">
            <Button type="submit" className="w-full" disabled={submitting}>
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : (
                "Sign up"
              )}
            </Button>
            <p className="text-xs text-muted-foreground text-center">
              Already have an account?{" "}
              <Link
                to="/login"
                className="underline underline-offset-2 decoration-accent-mint hover:text-foreground transition-colors"
              >
                Sign in
              </Link>
            </p>
            {turnstileSiteKey && (
              <>
                <div ref={turnstile.ref} data-testid="turnstile-widget" />
                {turnstile.failed && (
                  <Alert variant="destructive">
                    <AlertDescription>
                      Couldn&apos;t load the security check. Please refresh the
                      page or check your network connection.
                    </AlertDescription>
                  </Alert>
                )}
              </>
            )}
          </CardFooter>
        </form>
      </Card>
    </AuthLayout>
  );
}
